import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Box, Typography, LinearProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SparklesIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { keyframes } from "@mui/system";

const orbit = keyframes`
  0% { transform: rotate(0deg) scale(1); opacity: 0.7; }
  50% { transform: rotate(180deg) scale(1.1); opacity: 1; }
  100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
`;

const slideUpFade = keyframes`
  0% { opacity: 0; transform: translateY(10px); filter: blur(2px); }
  100% { opacity: 1; transform: translateY(0); filter: blur(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const PHASE_STRINGS: string[][] = [
  // Phase 1 — Understanding (0-15s)
  [
    "Reading your intent...",
    "Sketching the workflow shape...",
    "Mapping out the steps you'll need...",
  ],
  // Phase 2 — Discovery (15-45s)
  [
    "Searching the agent catalog...",
    "Finding agents for the job...",
    "Looking for the best match for each step...",
    "Reviewing candidate agents...",
  ],
  // Phase 3 — Selection (45-75s)
  [
    "Comparing agent capabilities...",
    "Picking the right agent for each step...",
    "Validating agent fit...",
    "Checking for the latest versions...",
  ],
  // Phase 4 — Assembly (75-120s)
  [
    "Wiring up the steps...",
    "Connecting inputs and outputs...",
    "Configuring data flow between agents...",
    "Designing the human approval points...",
  ],
  // Phase 5 — Validation (120-150s)
  [
    "Validating interoperability...",
    "Checking dependencies...",
    "Verifying every step has what it needs...",
  ],
  // Phase 6 — Final touches (150s+): single fixed message shown indefinitely
  [
    "Almost there...",
  ],
];

const PHASE_DURATIONS_MS = [5000, 6000, 7000, 8000, 9000];

function randomPhaseDuration(): number {
  return PHASE_DURATIONS_MS[Math.floor(Math.random() * PHASE_DURATIONS_MS.length)];
}

/** Pick one random string from each phase at mount time and return the flat sequence. */
function buildSequence(): string[] {
  return PHASE_STRINGS.map(
    (phase) => phase[Math.floor(Math.random() * phase.length)],
  );
}

const WorkflowGeneratingIndicator: React.FC = () => {
  // Sequence is fixed for the lifetime of this component mount
  const [sequence] = useState<string[]>(buildSequence);
  const [currentIndex, setCurrentIndex] = useState(0);
  const textRef = useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  // Advance through phases every PHASE_DURATION_MS; stay on last phase once reached
  useEffect(() => {
    if (currentIndex >= sequence.length - 1) return;
    const timer = window.setTimeout(() => {
      setCurrentIndex((i) => Math.min(i + 1, sequence.length - 1));
    }, randomPhaseDuration());
    return () => clearTimeout(timer);
  }, [currentIndex, sequence.length]);

  // Measure text width for matching progress bar
  useLayoutEffect(() => {
    if (textRef.current) {
      const { width } = textRef.current.getBoundingClientRect();
      if (width > 0) setTextWidth(width);
    }
  }, [currentIndex]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 320 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
        {/* Orbiting stars cluster — same as ThinkingIndicator */}
        <Box
          sx={{
            position: "relative",
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AutoAwesomeIcon
            sx={{
              color: "primary.main",
              fontSize: "2.2rem",
              animation: `${orbit} 4s infinite linear`,
              zIndex: 2,
            }}
          />
          <SparklesIcon
            sx={{
              position: "absolute",
              color: "primary.main",
              fontSize: "1.2rem",
              opacity: 0.5,
              animation: `${orbit} 3s infinite reverse linear`,
              top: -5,
              right: -5,
            }}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            minWidth: 0,
          }}
        >
          <Typography
            ref={textRef}
            component="span"
            variant="body1"
            key={currentIndex}
            sx={{
              fontWeight: 600,
              display: "inline-block",
              width: "fit-content",
              whiteSpace: "nowrap",
              background:
                "linear-gradient(90deg, #6D4C41 0%, #b06d50 50%, #6D4C41 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              animation: `${shimmer} 3s linear infinite, ${slideUpFade} 0.6s ease-out`,
            }}
          >
            {sequence[currentIndex]}
          </Typography>

          <LinearProgress
            sx={{
              width: textWidth ? `${textWidth}px` : "160px",
              height: 3,
              mt: 0.5,
              borderRadius: 2,
              bgcolor: "rgba(176, 109, 80, 0.1)",
              "& .MuiLinearProgress-bar": {
                bgcolor: "primary.main",
                opacity: 0.8,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default WorkflowGeneratingIndicator;
